import {defineMessages, MessageDescriptor} from 'react-intl'
import {useSelector} from 'react-redux'
import {iconsList} from '@vio/atlas/Icon'

import {getFreezeSelectedContext} from '../../modules/selector'
import {DealFreezeEndpoints} from '../../services/dealFreezeApi'
import {DealFreezeError, ErrorCases, PayloadErrorType} from './types'

const ERROR_TITLES = defineMessages({
  [ErrorCases.SoldOut]: {
    id: 'unavailability.ranOutOfRooms',
    defaultMessage:
      'Your dates are popular. We have run out of rooms at this property.'
  },
  /**
   * For now, price mismatch error is same as sold out error. In the future, there will be a
   * different design for price mismatch error that does not prevent the user from freezing.
   */
  [ErrorCases.PriceMismatch]: {
    id: 'unavailability.ranOutOfRooms',
    defaultMessage:
      'Your dates are popular. We have run out of rooms at this property.'
  },
  [ErrorCases.Unavailability]: {
    id: 'unavailability.ranOutOfRooms',
    defaultMessage:
      'Your dates are popular. We have run out of rooms at this property.'
  },
  [ErrorCases.LimitReached]: {
    id: 'dealFreeze.limitReachedErrorTitle',
    defaultMessage: "You've already frozen three deals"
  },
  [ErrorCases.UnexpectedError]: {
    id: 'dealFreeze.unexpectedError',
    defaultMessage: 'We are experiencing some issues'
  }
}) as Record<ErrorCases, MessageDescriptor>

const ERROR_MESSAGES = defineMessages({
  [ErrorCases.SoldOut]: {
    id: 'soldOut.messageWithDatesB',
    defaultMessage: 'Sold out {checkIn} - {checkOut}'
  },
  [ErrorCases.PriceMismatch]: {
    id: 'soldOut.messageWithDatesB',
    defaultMessage: 'Sold out {checkIn} - {checkOut}'
  },
  [ErrorCases.Unavailability]: {
    id: 'dealFreeze.unavailabilityError',
    defaultMessage: 'No availability {checkIn} - {checkOut}'
  },
  [ErrorCases.LimitReached]: {
    id: 'dealFreeze.limitReachedErrorTitle',
    defaultMessage: "You've already frozen three deals"
  },
  [ErrorCases.UnexpectedError]: {
    id: 'dealFreeze.unexpectedError',
    defaultMessage: 'We are experiencing some issues'
  }
}) as Record<ErrorCases, MessageDescriptor>

const ERROR_DESCRIPTION = defineMessages({
  [ErrorCases.LimitReached]: {
    id: 'dealFreeze.limitReachedErrorDescription',
    defaultMessage:
      'You can freeze three deals at once, cancel one of your frozen deals to freeze this one.'
  },
  [ErrorCases.UnexpectedError]: {
    id: 'dealFreeze.unexpectedErrorDescription',
    defaultMessage:
      'Our servers are currently experiencing difficulties. Please try again later.'
  }
}) as Record<ErrorCases, MessageDescriptor>

const CTA_MESSAGES = defineMessages({
  [ErrorCases.SoldOut]: {
    id: 'dealFreeze.unavailabilityCta',
    defaultMessage: 'Change dates'
  },
  [ErrorCases.PriceMismatch]: {
    id: 'dealFreeze.unavailabilityCta',
    defaultMessage: 'Change dates'
  },
  [ErrorCases.Unavailability]: {
    id: 'dealFreeze.changeRoomCta',
    defaultMessage: 'Change room'
  },
  [ErrorCases.LimitReached]: {
    id: 'payment.goBack',
    defaultMessage: 'Go back'
  },
  [ErrorCases.UnexpectedError]: {
    id: 'payment.goBack',
    defaultMessage: 'Go back'
  }
}) as Record<ErrorCases, MessageDescriptor>

const ERROR_ICONS = {
  [ErrorCases.SoldOut]: 'Forbid',
  [ErrorCases.PriceMismatch]: 'Forbid',
  [ErrorCases.Unavailability]: 'Forbid',
  [ErrorCases.LimitReached]: 'Lock',
  [ErrorCases.UnexpectedError]: 'FaceSad'
} as Record<ErrorCases, keyof typeof iconsList>

interface UseGetErrorMessagesProps {
  error: DealFreezeError
}

const getErrorMessagesByCase = (errorCase: ErrorCases) => {
  return {
    title: ERROR_TITLES[errorCase],
    message: ERROR_MESSAGES[errorCase],
    description: ERROR_DESCRIPTION[errorCase] || '',
    ctaText: CTA_MESSAGES[errorCase],
    icon: ERROR_ICONS[errorCase],
    errorCase
  }
}

export const useGetErrorMessageAndCase = ({
  error
}: UseGetErrorMessagesProps) => {
  const {rooms, sapiMatchedOffer} = useSelector(getFreezeSelectedContext)
  const allAvailableOffers = rooms?.flatMap(room => room.offers) || []

  const {payloadErrorType, endpoint} = error

  if (
    endpoint === DealFreezeEndpoints.OfferCheck ||
    endpoint === DealFreezeEndpoints.SapiRoom
  ) {
    if (payloadErrorType === PayloadErrorType.PriceMismatch) {
      // at this state it shows `Sold out`
      return getErrorMessagesByCase(ErrorCases.PriceMismatch)
    }

    // user selected offer does not exist in SAPI rooms response, but there are other rooms available
    if (!sapiMatchedOffer && allAvailableOffers.length > 0) {
      return getErrorMessagesByCase(ErrorCases.SoldOut)
    }

    return getErrorMessagesByCase(ErrorCases.Unavailability)
  }

  if (payloadErrorType === PayloadErrorType.UserLimitReached) {
    return getErrorMessagesByCase(ErrorCases.LimitReached)
  }

  return getErrorMessagesByCase(ErrorCases.UnexpectedError)
}
